import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MODELLI_CHAT, type ModelloChat } from '@masamune/shared';
import { ApiError } from '../../lib/api';
import { confirm } from '../../stores/confirm.store';
import { toast } from '../../stores/toast.store';
import { useAssistenteStore } from '../../stores/assistente.store';
import { scegliModello, statoAssistenteQuery, type StatoAssistente } from './assistenteApi';

const SONNET: ModelloChat = 'claude-sonnet-5-5';

/**
 * Il modello della chat accanto al nome di MUNE, nell'intestazione del pannello (30/10/2026;
 * Lorenzo: «un bottone per cambiare modello? Da haiku a sonnet?»). Due modelli, quindi un clic
 * passa all'altro: verso Sonnet con la conferma, perché costa venti volte Haiku e il tetto del
 * giorno finisce prima. La conversazione aperta ricomincia, come fa il server alla domanda dopo:
 * i pensieri di un modello non valgono per l'altro (improntaChat).
 */
export function ModelloTasto({ stato, occupato }: { stato: StatoAssistente; occupato: boolean }): JSX.Element | null {
  const qc = useQueryClient();
  const [cambio, setCambio] = useState(false);
  const adesso = stato.modelloChat ?? stato.modello;
  const qui = MODELLI_CHAT.find((m) => m.id === adesso);
  if (!qui) return null;
  const altro = MODELLI_CHAT.find((m) => m.id !== qui.id)!;

  const cambia = async () => {
    if (altro.id === SONNET) {
      const ok = await confirm({
        title: 'Passare a Sonnet?',
        body: `Sonnet ragiona meglio sulle richieste difficili, ma è più lento e costa circa 20 volte Haiku: il tetto di ${stato.budgetGiornoUsd.toLocaleString('it-IT')} $ al giorno basta per molte meno domande. La conversazione ricomincia da capo.`,
        confirmLabel: 'Passa a Sonnet',
        cancelLabel: 'Resta su Haiku',
        icon: 'euro',
      });
      if (!ok) return;
    }
    setCambio(true);
    try {
      const nuovo = await scegliModello(altro.id);
      qc.setQueryData(statoAssistenteQuery.queryKey, nuovo);
      const st = useAssistenteStore.getState();
      if (st.messaggi.length) st.nuovaConversazione();
      toast.ok(`MUNE usa ${altro.nome}`);
    } catch (e) {
      toast.alert('Modello non cambiato', e instanceof ApiError ? e.message : 'Controlla la connessione e riprova');
    } finally {
      setCambio(false);
    }
  };

  return (
    <button
      type="button"
      className="ast-modello"
      data-modello={qui.nome.toLowerCase()}
      onClick={() => void cambia()}
      disabled={occupato || cambio}
      title={`Modello: ${qui.nome}. Clic per passare a ${altro.nome}`}
      aria-label={`Modello ${qui.nome}: passa a ${altro.nome}`}
    >
      {qui.nome}
    </button>
  );
}
